import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  Modal,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
  theme,
} from 'antd';
import {
  AuditOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  SolutionOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  anonimizarDataRequest,
  bloquearDataRequest,
  getDataRequest,
  updateDataRequest,
} from 'api/dataRequests';
import InformeTitular from './InformeTitular';
import {
  canalLabel,
  estadoColor,
  estadoLabel,
  eventoLabel,
  tipoDescripcion,
  tipoLabel,
} from './solicitudesDatosLabels';
import { calcularPlazo, colorPlazo, estaCerrada, formatearFecha } from './vencimiento';

const { Text, Paragraph, Title } = Typography;
const { useToken } = theme;
const { TextArea } = Input;

// Drawer de detalle de una solicitud sobre datos personales.
// Contrato estándar del repo: { open, solicitud, onClose, onSaved }.
//
// El backend NO tiene máquina de estados (cualquier estado -> cualquier estado),
// así que la guía es responsabilidad de esta UI: mostramos sólo las acciones que
// tienen sentido desde el estado actual.

function SolicitudDatosDrawer({ open, solicitud, onClose, onSaved, readOnly }) {
  const { token } = useToken();
  const { modal, message } = App.useApp();

  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [resolverOpen, setResolverOpen] = useState(false);
  const [resolucion, setResolucion] = useState('');
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  const solicitudId = solicitud?.id;

  const cargar = useCallback(async () => {
    if (!solicitudId) return;
    setCargando(true);
    setErrorDetalle(null);
    try {
      const data = await getDataRequest(solicitudId);
      setDetalle(data);
    } catch (err) {
      setErrorDetalle(err?.response?.data?.mensaje || 'No pudimos cargar el detalle.');
      setDetalle(null);
    } finally {
      setCargando(false);
    }
  }, [solicitudId]);

  // Reset completo al abrir o al cambiar de solicitud: si no, el drawer muestra
  // el informe de la persona anterior mientras carga el nuevo.
  useEffect(() => {
    if (!open) return;
    setDetalle(null);
    setErrorDetalle(null);
    setResolverOpen(false);
    setResolucion('');
    setRechazarOpen(false);
    setMotivoRechazo('');
    cargar();
  }, [open, solicitudId, cargar]);

  if (!solicitud) return null;

  // Preferimos la fila del listado mientras el detalle está en vuelo: así el
  // encabezado se pinta de inmediato en vez de parpadear vacío.
  const actual = detalle?.solicitud ?? solicitud;
  const plazo = calcularPlazo(actual);
  const cerrada = estaCerrada(actual?.estado);

  const trasEscribir = async (mensaje) => {
    message.success(mensaje);
    await cargar();
    onSaved?.();
  };

  const manejarError = (err, porDefecto) => {
    message.error(err?.response?.data?.mensaje || err?.message || porDefecto);
  };

  const cambiarEstado = async (estado, mensaje) => {
    setGuardando(true);
    try {
      await updateDataRequest(solicitudId, { estado });
      await trasEscribir(mensaje);
    } catch (err) {
      manejarError(err, 'No pudimos actualizar la solicitud.');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarResolver = async () => {
    if (!resolucion.trim()) {
      message.warning('Escriba la respuesta que se le dará a la persona.');
      return;
    }
    setGuardando(true);
    try {
      await updateDataRequest(solicitudId, { estado: 'resuelta', resolucion: resolucion.trim() });
      setResolverOpen(false);
      setResolucion('');
      await trasEscribir('La solicitud quedó resuelta.');
    } catch (err) {
      manejarError(err, 'No pudimos resolver la solicitud.');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarRechazar = async () => {
    // El backend responde 400 si se rechaza sin motivo: lo exigimos acá para no
    // mandar una petición que ya sabemos que va a fallar.
    if (!motivoRechazo.trim()) {
      message.warning('Debe indicar por qué se rechaza la solicitud.');
      return;
    }
    setGuardando(true);
    try {
      await updateDataRequest(solicitudId, {
        estado: 'rechazada',
        motivo_rechazo: motivoRechazo.trim(),
      });
      setRechazarOpen(false);
      setMotivoRechazo('');
      await trasEscribir('La solicitud quedó rechazada.');
    } catch (err) {
      manejarError(err, 'No pudimos rechazar la solicitud.');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarBloquear = () => {
    modal.confirm({
      title: 'Suspender el uso de los datos',
      icon: <PauseCircleOutlined style={{ color: token.colorWarning }} />,
      content:
        'Dejaremos de usar los datos de esta persona mientras se revisa la solicitud. No se borra nada y se puede volver atrás.',
      okText: 'Suspender el uso',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await bloquearDataRequest(solicitudId);
          await trasEscribir('El uso de los datos quedó suspendido.');
        } catch (err) {
          manejarError(err, 'No pudimos suspender el uso de los datos.');
          throw err;
        }
      },
    });
  };

  const nombrePersona = actual?.solicitante_nombre || actual?.solicitante_rut || 'esta persona';
  const totalRevision = detalle?.informe?.revisionHumana?.length ?? 0;
  const totalApariciones = detalle?.informe?.apariciones?.length ?? 0;

  const confirmarAnonimizar = () => {
    modal.confirm({
      title: '¿Anonimizar los datos de esta persona?',
      icon: <DeleteOutlined style={{ color: token.colorError }} />,
      width: 560,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginXS }}>
          <Paragraph style={{ marginBottom: 0 }}>
            Se borrarán de forma permanente los datos que identifican a{' '}
            <Text strong>{nombrePersona}</Text> en {totalApariciones}{' '}
            {totalApariciones === 1 ? 'registro' : 'registros'}, junto con sus firmas y fotos.
          </Paragraph>
          <Alert
            type="error"
            showIcon
            message="Esta acción no se puede deshacer"
            description="No hay forma de recuperar los datos después. Los registros que la ley obliga a conservar se mantienen, pero sin los datos de la persona."
          />
          {totalRevision > 0 && (
            <Alert
              type="warning"
              showIcon
              message={
                totalRevision === 1
                  ? 'Queda 1 registro sin revisar'
                  : `Quedan ${totalRevision} registros sin revisar`
              }
              description="Son registros que coinciden solo por nombre o cuyo dato está dentro de un texto escrito a mano. No se anonimizan automáticamente: reviselos aparte."
            />
          )}
        </div>
      ),
      okText: 'Sí, anonimizar de forma permanente',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await anonimizarDataRequest(solicitudId);
          await trasEscribir('Los datos de la persona fueron anonimizados.');
        } catch (err) {
          manejarError(err, 'No pudimos anonimizar los datos.');
          throw err;
        }
      },
    });
  };

  const eventos = [...(detalle?.solicitud?.eventos ?? [])].sort(
    (a, b) => new Date(a?.createdAt ?? 0) - new Date(b?.createdAt ?? 0)
  );

  const acciones = [];
  if (!readOnly) {
    if (cerrada) {
      acciones.push(
        <Button
          key="reabrir"
          icon={<UndoOutlined />}
          loading={guardando}
          onClick={() => cambiarEstado('en_revision', 'La solicitud volvió a revisión.')}
        >
          Volver a revisar
        </Button>
      );
    } else {
      if (actual?.estado !== 'en_revision') {
        acciones.push(
          <Button
            key="revisar"
            icon={<SolutionOutlined />}
            loading={guardando}
            onClick={() => cambiarEstado('en_revision', 'La solicitud quedó en revisión.')}
          >
            Tomar y revisar
          </Button>
        );
      }
      if (actual?.estado !== 'bloqueada') {
        acciones.push(
          <Button key="bloquear" icon={<PauseCircleOutlined />} onClick={confirmarBloquear}>
            Suspender el uso
          </Button>
        );
      }
      acciones.push(
        <Button
          key="rechazar"
          danger
          icon={<CloseCircleOutlined />}
          onClick={() => setRechazarOpen(true)}
        >
          Rechazar
        </Button>
      );
      acciones.push(
        <Button
          key="resolver"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => setResolverOpen(true)}
        >
          Resolver
        </Button>
      );
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={860}
      destroyOnHidden
      title={
        <Space align="center" size="middle" style={{ minWidth: 0 }}>
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: token.borderRadius,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: token.colorPrimaryBg,
              color: token.colorPrimary,
              flexShrink: 0,
            }}
          >
            <AuditOutlined style={{ fontSize: 18 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Title level={5} style={{ margin: 0 }}>
              {tipoLabel(actual?.tipo)}
            </Title>
            <Space size={6} wrap>
              <Tag color={estadoColor(actual?.estado)} style={{ margin: 0 }}>
                {estadoLabel(actual?.estado)}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Solicitud N.° {actual?.id}
              </Text>
            </Space>
          </div>
        </Space>
      }
      footer={
        acciones.length ? (
          <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
            {acciones}
          </Space>
        ) : null
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginMD }}>
        {plazo?.vencida && (
          <Alert
            type="error"
            showIcon
            message="Esta solicitud está fuera del plazo legal"
            description={`${plazo.texto}. Resuélvala cuanto antes.`}
          />
        )}
        {!plazo?.vencida && plazo?.urgente && (
          <Alert type="warning" showIcon message={`${plazo.texto}.`} />
        )}

        <div>
          <Paragraph type="secondary" style={{ marginBottom: token.marginXS, fontSize: 13 }}>
            {tipoDescripcion(actual?.tipo) ??
              'La persona ejerce un derecho sobre sus datos personales.'}
          </Paragraph>
          <Descriptions
            size="small"
            column={2}
            bordered
            items={[
              { key: 'nombre', label: 'Nombre', children: actual?.solicitante_nombre || '—' },
              { key: 'rut', label: 'RUT', children: actual?.solicitante_rut || '—' },
              {
                key: 'email',
                label: 'Correo',
                children: actual?.solicitante_email || '—',
              },
              { key: 'canal', label: 'Llegó por', children: canalLabel(actual?.canal) },
              {
                key: 'recibida',
                label: 'Recibida',
                children: formatearFecha(actual?.recibida_at, true),
              },
              {
                key: 'vence',
                label: 'Plazo para responder',
                children: (
                  <Space size={6} wrap>
                    <span>{formatearFecha(actual?.vence_at)}</span>
                    {plazo?.texto && (
                      <Text style={{ color: colorPlazo(plazo, token), fontSize: 12 }}>
                        ({plazo.texto})
                      </Text>
                    )}
                  </Space>
                ),
              },
              ...(actual?.bloqueo_vence_at
                ? [
                    {
                      key: 'bloqueo',
                      label: 'Suspensión vigente hasta',
                      children: formatearFecha(actual.bloqueo_vence_at),
                    },
                  ]
                : []),
              ...(actual?.resuelta_at
                ? [
                    {
                      key: 'resuelta',
                      label: 'Cerrada el',
                      children: formatearFecha(actual.resuelta_at, true),
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {actual?.detalle && (
          <div>
            <Text strong style={{ fontSize: 13 }}>
              Lo que pide la persona
            </Text>
            <Paragraph
              style={{
                marginTop: token.marginXXS,
                marginBottom: 0,
                padding: token.paddingSM,
                background: token.colorFillAlter,
                borderRadius: token.borderRadius,
                whiteSpace: 'pre-wrap',
              }}
            >
              {actual.detalle}
            </Paragraph>
          </div>
        )}

        {actual?.resolucion && (
          <Alert
            type="success"
            showIcon
            message="Respuesta entregada"
            description={<span style={{ whiteSpace: 'pre-wrap' }}>{actual.resolucion}</span>}
          />
        )}
        {actual?.motivo_rechazo && (
          <Alert
            type="error"
            showIcon
            message="Motivo del rechazo"
            description={<span style={{ whiteSpace: 'pre-wrap' }}>{actual.motivo_rechazo}</span>}
          />
        )}

        <Divider style={{ margin: `${token.marginXS}px 0` }} />

        <div>
          <Text strong style={{ fontSize: 13 }}>
            Dónde aparecen sus datos
          </Text>
          <div style={{ marginTop: token.marginXS }}>
            {cargando && <Skeleton active paragraph={{ rows: 4 }} />}
            {!cargando && errorDetalle && (
              <Alert
                type="error"
                showIcon
                message="No pudimos cargar el detalle"
                description={errorDetalle}
                action={
                  <Button size="small" onClick={cargar}>
                    Reintentar
                  </Button>
                }
              />
            )}
            {!cargando && !errorDetalle && <InformeTitular informe={detalle?.informe} />}
          </div>
        </div>

        <div>
          <Text strong style={{ fontSize: 13 }}>
            Historial
          </Text>
          <div style={{ marginTop: token.marginSM }}>
            {cargando && <Skeleton active paragraph={{ rows: 2 }} />}
            {!cargando && !eventos.length && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Todavía no hay movimientos registrados."
              />
            )}
            {!cargando && eventos.length > 0 && (
              <Timeline
                items={eventos.map((evento) => ({
                  key: evento?.id,
                  color: token.colorPrimary,
                  children: (
                    <div style={{ minWidth: 0 }}>
                      <Text style={{ fontSize: 13 }}>{eventoLabel(evento?.tipo)}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatearFecha(evento?.createdAt, true)}
                      </Text>
                      {evento?.detalle && (
                        <Paragraph
                          type="secondary"
                          style={{ marginBottom: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
                        >
                          {evento.detalle}
                        </Paragraph>
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </div>
        </div>

        {!readOnly && (
          <>
            <Divider style={{ margin: `${token.marginXS}px 0` }} />
            <div
              style={{
                border: `1px solid ${token.colorErrorBorder}`,
                borderRadius: token.borderRadiusLG,
                padding: token.paddingSM,
                background: token.colorErrorBg,
              }}
            >
              <Text strong style={{ fontSize: 13, color: token.colorErrorText }}>
                Anonimizar los datos
              </Text>
              <Paragraph style={{ fontSize: 12, marginBottom: token.marginXS, marginTop: 4 }}>
                Borra de forma permanente los datos que identifican a la persona en todos los
                registros del listado de arriba. No se puede deshacer.
              </Paragraph>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                disabled={cargando || !!errorDetalle}
                onClick={confirmarAnonimizar}
              >
                Anonimizar
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal
        open={resolverOpen}
        title="Resolver la solicitud"
        okText="Resolver"
        cancelText="Cancelar"
        confirmLoading={guardando}
        onOk={confirmarResolver}
        onCancel={() => setResolverOpen(false)}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Escriba la respuesta que se le entregará a la persona. Queda guardada como respaldo de
          cómo se resolvió.
        </Paragraph>
        <TextArea
          rows={5}
          value={resolucion}
          maxLength={2000}
          showCount
          onChange={(e) => setResolucion(e.target.value)}
          placeholder="Ej.: Se le envió por correo una copia de todos sus datos el 12 de julio."
        />
      </Modal>

      <Modal
        open={rechazarOpen}
        title="Rechazar la solicitud"
        okText="Rechazar"
        okButtonProps={{ danger: true }}
        cancelText="Cancelar"
        confirmLoading={guardando}
        onOk={confirmarRechazar}
        onCancel={() => setRechazarOpen(false)}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Indique por qué se rechaza. El motivo es obligatorio y se le comunica a la persona.
        </Paragraph>
        <TextArea
          rows={5}
          value={motivoRechazo}
          maxLength={2000}
          showCount
          onChange={(e) => setMotivoRechazo(e.target.value)}
          placeholder="Ej.: No pudimos confirmar la identidad de quien hace la solicitud."
        />
      </Modal>
    </Drawer>
  );
}

export default SolicitudDatosDrawer;
